import { defineStore } from 'pinia';
import { reactive } from 'vue';
import { DetailAggregateBusinessData, DetailAlertData, DetailBaseBusinessData, IInventoryDashboardState } from './types';
import { initInventoryDashboardState } from './initValue';
import { DimensionType, DimensionTypes, MetricType, MetricTypes } from '@/inventoryDashboardModule/domains/entities/Dashboard';
import { IYearMonth } from '@/coreDomainsModule/date';
import { diContainer } from '@/coreContainer/container';
import { FilterContentItem } from '@/corePagesModule/filterModal/filterModal';
import { IPagingOptions } from '@/coreDomainsModule/paging';
import { Kpi } from '@/inventoryDashboardModule/domains/entities/Kpi';
import { DashboardService } from '@/inventoryDashboardModule/domains/services/DashboardService';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { DashboardAlertSettingInSeverity } from '@/inventoryDashboardModule/domains/entities/DashboardAlert';
/**
 * 在庫削減ダッシュボードのStore
 */
export const useInventoryDashboardStore = defineStore('inventoryDashboard', () => {
  const state = reactive<IInventoryDashboardState>(initInventoryDashboardState());

  /**
   * 表示年月を変更する
   * @param value - 変更する表示年月
   */
  const changeDisplayYearMonth = async (value: IYearMonth) => {
    const fetchFlag =
      DashboardService.isMetricTypeAndDimensionTypeIsValid(state.information.metricType, state.information.dimensionType) &&
      DateService.compareYearMonths(value, state.displayYearMonth);
    state.displayYearMonth = value;
    if (fetchFlag) await getByDimensionData();
  };

  /**
   * 表示期間を変更する
   * @param value - 変更する表示期間
   */
  const changeDisplayYearMonthPeriod = async (value: [IYearMonth | undefined, IYearMonth | undefined]) => {
    const fetchFlag =
      DashboardService.isMetricTypeAndDimensionTypeIsValid(state.information.metricType, state.information.dimensionType) &&
      ((value[0] && DateService.compareYearMonths(value[0], state.displayYearMonthPeriod[0])) ||
        (value[1] && DateService.compareYearMonths(value[1], state.displayYearMonthPeriod[1])));
    state.displayYearMonthPeriod = [value[0] ?? state.displayYearMonthPeriod[0], value[1] ?? state.displayYearMonthPeriod[1]];
    if (fetchFlag) await getByDimensionData();
  };

  /**
   * 内示考慮期間を変更する
   * @param value - 変更する内示考慮期間
   */
  const changeForecastAccuracyPeriod = async (value: number | undefined) => {
    const fetchFlag =
      DashboardService.isMetricTypeAndDimensionTypeIsValid(state.information.metricType, state.information.dimensionType) &&
      state.forecastAccuracyPeriod !== value;
    state.forecastAccuracyPeriod = value;
    if (fetchFlag) await getByDimensionData();
  };

  /**
   * 分析軸の種類を変更する
   * @param value - 変更したDimensionType型の分析軸種類
   */
  const changeDimensionType = async (value: DimensionType) => {
    const fetchFlag =
      DashboardService.isMetricTypeAndDimensionTypeIsValid(state.information.metricType, value) &&
      state.information.dimensionType !== value;

    state.information.dimensionType = value;

    if (fetchFlag) await getByDimensionData();
  };

  /**
   * 分析するデータを変更する
   * @param value - 変更したMetricType型の分析データ種類
   */
  const changeMetricType = async (value: MetricType) => {
    const fetchFlag =
      DashboardService.isMetricTypeAndDimensionTypeIsValid(value, state.information.dimensionType) &&
      !DashboardService.isMetricTypesSameBusinessType(state.information.metricType, value).result;

    state.information.metricType = value;

    if (fetchFlag) await getByDimensionData();
  };

  /**
   * 在庫削減ダッシュボードの概要データを取得する
   */
  const getOverviewData = async () => {
    const data = await diContainer.getInventoryDashboardOverviewUseCase.execute();
    switch (data.status.type) {
      case 'success':
        state.overviewData = data.status.data;
    }
  };

  /**
   * overviewの詳細情報(集計取引)を取得する
   * @param sortType - ソートタイプ
   * @param sortTarget - ソートの対象
   * @param limit - 制限
   * @param offset - オフセット
   * @param filterItem - フィルター情報
   * @returns - ページング情報とコンテンツの配列
   */
  const getDetailAggregateBusinessData = async (
    sortType?: string,
    sortTarget?: string,
    limit?: number,
    offset?: number,
    filterItem?: FilterContentItem[]
  ): Promise<[DetailAggregateBusinessData[], IPagingOptions] | null> => {
    const data = await diContainer.getInventoryDashboardDetailAggBizUseCase.execute(sortType, sortTarget, limit, offset, filterItem);
    switch (data.status.type) {
      case 'success':
        return data.status.data;
      case 'error':
        return null;
    }
  };

  /**
   * overviewの詳細情報(集計取引)を取得する
   * @param severity - 重大度
   * @param direction - アラート向き
   * @param sortType - ソートタイプ
   * @param sortTarget - ソートの対象
   * @param limit - 制限
   * @param offset - オフセット
   * @param filterItem - フィルター情報
   * @param alertFilterInfo - アラートのフィルタ情報
   * @param alertSettings - アラートの設定
   * @returns - ページング情報とコンテンツの配列
   */
  const getDetailAggregateBusinessWithAlertData = async (
    severity: 'critical' | 'warning',
    direction: 'excess' | 'shortage',
    sortType?: string,
    sortTarget?: string,
    limit?: number,
    offset?: number,
    filterItem?: FilterContentItem[],
    alertFilterInfo?: string[],
    alertSettings?: DashboardAlertSettingInSeverity
  ): Promise<[DetailAlertData, IPagingOptions] | null> => {
    const data = await diContainer.getInventoryDashboardDetailAggBizWithAlertUseCase.execute(
      severity,
      direction,
      sortType,
      sortTarget,
      limit,
      offset,
      filterItem,
      alertFilterInfo,
      alertSettings
    );
    switch (data.status.type) {
      case 'success':
        return data.status.data;
      case 'error':
        return null;
    }
  };

  /**
   * overviewの詳細情報(関連取引)を取得する
   * @param displayYearMonth - 表示基準月
   * @param sortType - ソートタイプ
   * @param sortTarget - ソートの対象
   * @param limit - 制限
   * @param offset - オフセット
   * @param isExcludeNoAccuracy - 内示正答率がない取引を省くかどうか
   * @param filterItem - フィルター情報
   * @returns - ページング情報とコンテンツの配列
   */
  const getDetailBaseBusinessData = async (
    displayYearMonth: IYearMonth,
    sortType?: string,
    sortTarget?: string,
    limit?: number,
    offset?: number,
    isExcludeNoAccuracy?: boolean,
    filterItem?: FilterContentItem[]
  ): Promise<[DetailBaseBusinessData[], IPagingOptions] | null> => {
    const data = await diContainer.getInventoryDashboardDetailBaseBizUseCase.execute(
      displayYearMonth,
      sortType,
      sortTarget,
      limit,
      offset,
      isExcludeNoAccuracy,
      filterItem
    );
    switch (data.status.type) {
      case 'success':
        return data.status.data;
      case 'error':
        return null;
    }
  };

  /**
   * KPI設定のために仕入先担当者一覧を取得する
   */
  const getAggregateBusinessResponsibleUsersForKpi = async () => {
    const data = await diContainer.getAggregateBusinessResponsibleUsersForKpiUseCase.execute();
    switch (data.status.type) {
      case 'success':
        return data.status.data;
      case 'error':
        return null;
    }
  };

  /**
   * KPI設定画面向けにKPI一覧を取得する
   */
  const getKpiData = async () => {
    // 一旦この組み合わせで固定
    const data = await diContainer.getKpiUseCase.execute(MetricTypes.Turnover, [DimensionTypes.AggregateBusinessResponsibleUser]);
    switch (data.status.type) {
      case 'success':
        return data.status.data;
      case 'error':
        return null;
    }
  };

  /**
   * KPI設定内容を保存する
   * @param kpis - 保存対象のKPI配列
   */
  const postKpiData = async (kpis: Kpi[]) => {
    const data = await diContainer.postKpiUseCase.execute(kpis);
    switch (data.status.type) {
      case 'success':
        return true;
      case 'error':
        return false;
    }
  };

  /**
   * 現在の指標/切り口設定で切り口別データを取得する
   */
  const getByDimensionData = async () => {
    if (DashboardService.isUnknown(state.information.metricType) || DashboardService.isUnknown(state.information.dimensionType)) return;
    state.informationLoadingState.status.type = 'loading';
    if (DashboardService.isMetricTypeByAggBiz(state.information.metricType)) {
      await _getByDimensionAggregateBusinessData();
    } else {
      await _getByDimensionBaseBusinessData();
    }
  };

  /**
   * 集計取引の切り口別データを取得して状態に反映する
   */
  const _getByDimensionAggregateBusinessData = async () => {
    const data = await diContainer.getByDimensionAggBizUseCase.execute(
      state.information.metricType,
      state.information.dimensionType,
      state.displayYearMonth.year,
      state.displayYearMonth.month,
      state.displayYearMonthPeriod,
      state.forecastAccuracyPeriod,
      undefined,
      undefined,
      undefined,
      undefined,
      state.filterItems
    );
    switch (data.status.type) {
      case 'success':
        state.informationLoadingState.status.type = 'success';
        state.information = data.status.data;
        return;
      default:
        state.informationLoadingState.status.type = 'error';
        return;
    }
  };

  /**
   * 関連取引の切り口別データを取得して状態に反映する
   */
  const _getByDimensionBaseBusinessData = async () => {
    const data = await diContainer.getByDimensionBaseBizUseCase.execute(
      state.information.metricType,
      state.information.dimensionType,
      state.displayYearMonth.year,
      state.displayYearMonth.month,
      state.displayYearMonthPeriod,
      undefined,
      undefined,
      undefined,
      undefined,
      state.filterItems
    );
    switch (data.status.type) {
      case 'success':
        state.informationLoadingState.status.type = 'success';
        state.information = data.status.data;
        return;
      default:
        state.informationLoadingState.status.type = 'error';
        return;
    }
  };

  return {
    state,
    changeDisplayYearMonth,
    changeDisplayYearMonthPeriod,
    changeForecastAccuracyPeriod,
    changeDimensionType,
    changeMetricType,
    getOverviewData,
    getDetailAggregateBusinessData,
    getDetailAggregateBusinessWithAlertData,
    getDetailBaseBusinessData,
    getAggregateBusinessResponsibleUsersForKpi,
    getKpiData,
    postKpiData,
    getByDimensionData,
  };
});
